/**
 * Thread isolation helpers (PR fix/thread-isolation-per-thread-state)
 *
 * Pure helpers extracted from `useGlobalChat`'s reducer so the
 * thread-isolation invariants can be unit-tested without mounting
 * Firebase auth, the platform message service, and the rest of the
 * GlobalChat dependency graph.
 *
 * The functions here implement the per-thread `processingByThread` /
 * `processingStartedAtByThread` map updates that drive the chip-level
 * spinner. Without these:
 *   • the active chip's spinner reads the GLOBAL `isProcessing` flag
 *     and lights up whenever ANY thread is mid-stream — so a request
 *     on thread B causes thread A's chip to spin (and vice-versa);
 *   • `stopProcessing()` clears the GLOBAL flag, so finishing a
 *     stream on thread A also stops thread B's spinner even when B
 *     is still streaming.
 *
 * The reducer wires these helpers into the `SET_PROCESSING` action
 * (see `chatReducer` in `useGlobalChat.ts`). Callers can also use
 * them directly when constructing test fixtures or persistence
 * snapshots.
 */

export interface ProcessingThreadState {
  /** Map<threadId, true> — exactly the threads with an in-flight request. */
  processingByThread: Record<string, boolean>;
  /**
   * Map<threadId, startedAtMs> — the HEARTBEAT stamp, re-written on every
   * `SET_PROCESSING:true`. Long turns re-call startProcessing() every few
   * minutes to keep this fresh so the 10-min safety-timeout floor does not
   * release the send button (see useGlobalChat PROCESSING_SAFETY_TIMEOUT_MS).
   * DO NOT read this for the elapsed clock — use `turnStartedAtByThread`.
   */
  processingStartedAtByThread: Record<string, number>;
  /**
   * Map<threadId, turnStartedAtMs> — the CLOCK epoch, stamped ONCE on the
   * false→true rising edge and never moved while the turn runs (cleared on
   * stop). `processingStartedAtByThread` cannot serve the elapsed clock
   * because it is re-stamped mid-turn (heartbeat + every startStreamingMessage),
   * which reset the in-chip clock to ~0 ("working · 0:00" stuck / freeze after
   * the first streaming bubble). Optional for back-compat with callers/fixtures
   * that predate this field; `applyProcessingState` always returns it populated.
   */
  turnStartedAtByThread?: Record<string, number>;
  /** Derived: any thread processing → true. */
  isProcessing: boolean;
  /** Derived: oldest in-flight start time across all threads, or null. */
  processingStartedAt: number | null;
}

/**
 * Fold a per-thread processing state update into a fresh
 * `ProcessingThreadState`. Mutating-style: pass the current state and
 * the requested change; receive a new state with derived fields
 * recomputed.
 *
 * Calling with `threadId === null` (or undefined) takes the legacy
 * global path:
 *   • set to true → mark global flag, leave per-thread map alone
 *   • set to false → clear ONLY the legacy global flag; per-thread
 *     entries are preserved and the derived global recomputed from them
 *
 * THREAD-ISOLATION (2026-07-10): the thread-less stop previously wiped
 * the whole per-thread map. Any finalize firing outside a send context
 * (resume flows, late async finalizes — currentSendThreadIdRef already
 * restored to null) therefore stopped EVERY thread's spinner: the exact
 * "both spinners stopped" cross-thread repro. Per-thread entries are now
 * cleared only by their own thread-keyed stop or the safety floor.
 *
 * Use the per-thread path (with a threadId) for the canonical send
 * pipeline. The legacy path exists only for callers that have not
 * been threaded yet.
 */
export function applyProcessingState(
  prev: ProcessingThreadState,
  next: {
    isProcessing: boolean;
    threadId?: string | null;
    timestamp?: number;
    /** Rising-edge clock epoch override (2026-08-23): when discovery re-arms
     *  a chip for a long-running SERVER task, the visible clock should show
     *  the task's true age (ActiveTaskSummary.createdAtMs), not restart at
     *  0:00 — while the HEARTBEAT stamp must stay fresh (an old heartbeat
     *  would trip the 10-min safety floor immediately). Only consulted on
     *  the rising edge; ignored while a clock epoch already exists. */
    turnEpochMs?: number;
  },
): ProcessingThreadState {
  const { isProcessing, threadId, timestamp, turnEpochMs } = next;

  if (threadId) {
    const nextByThread = { ...prev.processingByThread };
    const nextStartedAt = { ...prev.processingStartedAtByThread };
    const nextTurnStartedAt = { ...(prev.turnStartedAtByThread || {}) };
    if (isProcessing) {
      nextByThread[threadId] = true;
      // Heartbeat stamp — re-written every time, so long turns keep the
      // safety-timeout floor fresh.
      nextStartedAt[threadId] = timestamp || Date.now();
      // Clock epoch — rising-edge ONLY. startProcessing() is re-dispatched
      // throughout a turn (send-start, every startStreamingMessage, long-turn
      // heartbeats); stamping the elapsed epoch on each of those reset the
      // in-chip clock to ~0 mid-turn. Preserve the first (turn-start) value.
      if (nextTurnStartedAt[threadId] == null) {
        nextTurnStartedAt[threadId] = turnEpochMs || timestamp || Date.now();
      }
    } else {
      delete nextByThread[threadId];
      delete nextStartedAt[threadId];
      delete nextTurnStartedAt[threadId];
    }
    const startedAtValues = Object.values(nextStartedAt);
    const anyProcessing = Object.values(nextByThread).some(Boolean);
    return {
      processingByThread: nextByThread,
      processingStartedAtByThread: nextStartedAt,
      turnStartedAtByThread: nextTurnStartedAt,
      isProcessing: anyProcessing,
      processingStartedAt: anyProcessing && startedAtValues.length > 0
        ? Math.min(...startedAtValues)
        : null,
    };
  }

  // Legacy global path.
  if (!isProcessing) {
    // Thread-less stop: clear only the legacy global flag. Other threads'
    // in-flight state survives; the derived global reflects what remains.
    const anyProcessing = Object.values(prev.processingByThread).some(Boolean);
    const startedAtValues = Object.values(prev.processingStartedAtByThread);
    return {
      processingByThread: prev.processingByThread,
      processingStartedAtByThread: prev.processingStartedAtByThread,
      turnStartedAtByThread: prev.turnStartedAtByThread || {},
      isProcessing: anyProcessing,
      processingStartedAt: anyProcessing && startedAtValues.length > 0
        ? Math.min(...startedAtValues)
        : null,
    };
  }
  return {
    processingByThread: prev.processingByThread,
    processingStartedAtByThread: prev.processingStartedAtByThread,
    turnStartedAtByThread: prev.turnStartedAtByThread || {},
    isProcessing: true,
    processingStartedAt: timestamp || Date.now(),
  };
}

/**
 * Detect an ORPHANED global processing flag that must not gate the
 * send button.
 *
 * Root cause (Symptom 5, Miles 2026-07-10 screenshot: a brand-new
 * "New thread" with the composer greyed out and a stuck
 * "please wait while processing…" popup):
 *
 * The authoritative gate `isSystemProcessing()` only consults the
 * per-thread processing/pending maps when at least one of them is
 * non-empty. When BOTH maps are empty but the legacy global
 * `state.isProcessing` flag is still `true`, the gate falls through and
 * trusts that global flag — blocking a genuinely idle thread for up to
 * the full PROCESSING_SAFETY_TIMEOUT_MS (10 min) until the safety floor
 * auto-clears it.
 *
 * That empty-maps + global-`true` combination is, by construction, an
 * ORPHAN: every real in-flight turn in the multi-thread architecture
 * keys `processingByThread` via `startProcessing(originatingThreadId)`.
 * The one remaining ambiguity is a *legacy single-thread* turn that set
 * the global flag with no thread context (bare `startProcessing()`).
 * Such a turn ALWAYS renders a `pendingShimmer`/`pendingSpin` widget,
 * so `hasRenderedPendingWidget` disambiguates it: a real legacy turn
 * shows a spinner (keep blocking); a leaked flag shows nothing
 * (safe to unblock).
 *
 * This is deliberately NON-MUTATING — it does not clear the flag. It
 * only decides that the send button should not be gated on an orphaned
 * flag, preserving the "never auto-clear mid-chain" invariant that
 * fixed Miles's earlier "nothing is spinning, is it paused?" bug.
 */
export function isOrphanedGlobalProcessingFlag(params: {
  isProcessing: boolean;
  hasPerThreadProcessing: boolean;
  hasPerThreadPending: boolean;
  hasRenderedPendingWidget: boolean;
}): boolean {
  const {
    isProcessing,
    hasPerThreadProcessing,
    hasPerThreadPending,
    hasRenderedPendingWidget,
  } = params;
  // Only meaningful when the global flag is set...
  if (!isProcessing) return false;
  // ...and there is NO per-thread attribution for it...
  if (hasPerThreadProcessing || hasPerThreadPending) return false;
  // ...and nothing is actually rendering a pending/streaming widget.
  if (hasRenderedPendingWidget) return false;
  return true;
}

/**
 * Phase 5 — explicit thread context. The message/streaming helpers used to
 * resolve their originating thread SOLELY from the shared, mutable
 * `currentSendThreadIdRef`, which is last-writer-wins across concurrent sends:
 * a helper (or an async continuation) fired for thread A after thread B pinned
 * the ref would misroute A's bubble/finalize into thread B. Callers now pass
 * the thread id EXPLICITLY (captured in the send's closure), and the ref is
 * only a fallback for genuinely context-free callers.
 *
 * Contract: if `explicit` was PASSED (any value, including `null` for
 * single-thread mode) it wins; only an OMITTED arg (`undefined`) falls back to
 * the ref. This lets a caller assert "single-thread, no originating thread"
 * (`null`) distinctly from "I have no context, use the ref" (`undefined`).
 */
export function resolveOriginatingThreadId(
  explicit: string | null | undefined,
  fallbackRefValue: string | null,
): string | null {
  return explicit !== undefined ? (explicit ?? null) : fallbackRefValue;
}

/**
 * Decide whether a streaming chunk targeting `originatingThreadId`
 * should be routed via the React dispatch path (active thread, normal
 * state.messages mutation) or written directly into the originating
 * thread's per-thread localStorage.
 *
 * When the active thread has changed mid-stream, dispatching into
 * `state.messages` would either silently drop the update (the
 * originating message id no longer lives in the active thread's
 * messages) or, worse, mutate the wrong thread's history. Routing to
 * per-thread localStorage keeps the originating thread's transcript
 * complete; switching back to it via `useChatThreads.switchThread`
 * naturally surfaces the latest content.
 *
 * Single-thread mode (`originatingThreadId === null`) always returns
 * `false` — there is no "other thread" to route to.
 */
export function shouldRouteStreamingUpdateToStorage(
  originatingThreadId: string | null,
  activeThreadId: string | null,
): boolean {
  if (!originatingThreadId) return false;
  return activeThreadId !== originatingThreadId;
}

/**
 * Identifier prefix used in DEBUG_THREAD_ISOLATION debug logs. Doubles
 * as a build-time grep marker so the live-bundle verification step in
 * the PR description can confirm the fix shipped — matches PR #71's
 * `[threadLabel]` pattern.
 */
export const THREAD_ISO_DEBUG_TAG = '[threadIso]';

/**
 * Decide whether a NEW message creation (ADD_MESSAGE or
 * startStreamingMessage) should be routed directly into the
 * originating thread's per-thread localStorage instead of dispatched
 * into the shared `state.messages` reducer slot.
 *
 * The reducer slot represents the ACTIVE thread's view; any
 * ADD_MESSAGE landed there while the active thread differs from the
 * originating thread becomes a ghost bubble in the wrong thread —
 * the empty/cross-pollinated bubble bug Miles reported on the live
 * 5/10/2026 demo.
 *
 * Returns `true` when:
 *   • there is a known originating thread id (a send is currently
 *     in flight or `currentSendThreadIdRef` is otherwise pinned), AND
 *   • the active thread differs from the originating thread, AND
 *   • per-thread storage hooks are available so we can actually
 *     route the write somewhere durable.
 *
 * Single-thread mode (no originating thread, or no active thread)
 * always returns `false`.
 */
export function shouldRouteNewMessageToStorage(
  originatingThreadId: string | null,
  activeThreadId: string | null,
  hasStorageHooks: boolean,
): boolean {
  if (!originatingThreadId) return false;
  if (!activeThreadId) return false;
  if (originatingThreadId === activeThreadId) return false;
  if (!hasStorageHooks) return false;
  return true;
}

/**
 * Cross-thread history-fallback tripwire (2026-06-11 backlog item 2.3).
 *
 * A turn that originates on thread A while thread B is active MUST read
 * its conversation history from A's per-thread storage. When the
 * per-thread reader is not wired (ref not yet mounted, or thread switcher
 * disabled), callers fall back to `state.messages` — the ACTIVE thread's
 * view — which is exactly the "Prest0n confused about which message/task
 * belongs to which thread" contamination symptom. This helper makes that
 * fallback loud in session logs instead of silent.
 *
 * Returns true when the fallback (and warning) fired — exported so the
 * tripwire condition itself is unit-testable.
 */
export function warnOnCrossThreadHistoryFallback(params: {
  originatingThreadId: string | null;
  activeThreadId: string | null;
  hasPerThreadReader: boolean;
}): boolean {
  const { originatingThreadId, activeThreadId, hasPerThreadReader } = params;
  if (!originatingThreadId) return false;
  if (activeThreadId === originatingThreadId) return false;
  if (hasPerThreadReader) return false;
  console.warn(
    `${THREAD_ISO_DEBUG_TAG} CROSS-THREAD HISTORY FALLBACK: send originated on a non-active thread but no per-thread reader is wired — using ACTIVE thread history (contamination risk)`,
    { originatingThreadId, activeThreadId },
  );
  return true;
}

export function resolveSendHistoryFailClosed(params: {
  originatingThreadId: string | null;
  activeThreadId: string | null;
  activeHistory: unknown[];
  readThreadMessages: ((threadId: string) => unknown[]) | null;
}): unknown[] {
  const {
    originatingThreadId,
    activeThreadId,
    activeHistory,
    readThreadMessages,
  } = params;

  if (!originatingThreadId) return Array.isArray(activeHistory) ? activeHistory : [];
  if (originatingThreadId === activeThreadId) return Array.isArray(activeHistory) ? activeHistory : [];
  if (readThreadMessages) {
    const fromThread = readThreadMessages(originatingThreadId);
    return Array.isArray(fromThread) ? fromThread : [];
  }

  warnOnCrossThreadHistoryFallback({
    originatingThreadId,
    activeThreadId,
    hasPerThreadReader: false,
  });
  return [];
}
